import apiSlice from '../api/api.slice';
import {
  IProjectGroupWithDetails,
  IProjectGroupWithDetailsAdmin,
  IProjectGroupLight,
  IProjectGroupPartsData,
  IProjectGroupFilters,
  IProjectGroupListResponse,
  ICreateProjectGroupRequest,
  ICreateProjectGroupResponse,
  IUpdateProjectGroupRequest,
  IUpdateProjectGroupResponse,
  IUpdateProjectGroupProjectsRequest,
  IDeleteProjectGroupResponse,
  ISubmitProjectGroupResponse,
  IDownloadProjectGroupResponse,
  IAdminPreviewProjectGroupResponse,
} from './projectGroup.d';
import { IPostWithUserAndAttachments } from '../post/post.d';

const projectGroupApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get all project groups with pagination
    getProjectGroups: builder.query<
      IProjectGroupListResponse,
      IProjectGroupFilters
    >({
      query: (params) => ({
        url: '/api/v1/project-groups',
        method: 'GET',
        params,
      }),
      transformResponse: (response: IProjectGroupListResponse) => response,
    }),

    // Get a single project group by ID
    // Supports light mode for optimized initial load (thumbnails only)
    getProjectGroupById: builder.query<
      IProjectGroupWithDetails | IProjectGroupLight,
      { id: string; light?: boolean }
    >({
      query: ({ id, light = false }) => ({
        url: `/api/v1/project-groups/${id}${light ? '?mode=light' : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        group: IProjectGroupWithDetails | IProjectGroupLight;
      }) => response.group,
    }),

    // Get full project details for a specific project within a group
    // Used for lazy loading project attachments on-demand
    getProjectDetails: builder.query<
      IPostWithUserAndAttachments,
      { groupId: string; projectId: string }
    >({
      query: ({ groupId, projectId }) => ({
        url: `/api/v1/project-groups/${groupId}/projects/${projectId}`,
        method: 'GET',
      }),
      transformResponse: (response: { project: IPostWithUserAndAttachments }) =>
        response.project,
    }),

    // Get aggregated parts and tools data for a project group
    // Used for deferred loading of parts section
    getProjectGroupParts: builder.query<IProjectGroupPartsData, string>({
      query: (groupId) => ({
        url: `/api/v1/project-groups/${groupId}/parts`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IProjectGroupPartsData }) =>
        response.data,
    }),

    // Create a new project group
    createProjectGroup: builder.mutation<
      ICreateProjectGroupResponse,
      ICreateProjectGroupRequest
    >({
      query: (data) => ({
        url: '/api/v1/project-groups',
        method: 'POST',
        body: data,
      }),
    }),

    // Update a project group's basic details
    updateProjectGroup: builder.mutation<
      IUpdateProjectGroupResponse,
      { id: string; data: IUpdateProjectGroupRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/project-groups/${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    // Update a project group's projects list
    updateProjectGroupProjects: builder.mutation<
      IUpdateProjectGroupResponse,
      { id: string; data: IUpdateProjectGroupProjectsRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/project-groups/${id}/projects`,
        method: 'PUT',
        body: data,
      }),
    }),

    // Submit an inline-authored group and all its workpieces for review
    submitProjectGroup: builder.mutation<ISubmitProjectGroupResponse, string>({
      query: (id) => ({
        url: `/api/v1/project-groups/${id}/submit`,
        method: 'POST',
      }),
    }),

    // Delete a project group
    deleteProjectGroup: builder.mutation<IDeleteProjectGroupResponse, string>({
      query: (id) => ({
        url: `/api/v1/project-groups/${id}`,
        method: 'DELETE',
      }),
    }),

    // Download all parts from a project group as ZIP
    downloadProjectGroup: builder.query<IDownloadProjectGroupResponse, string>({
      query: (id) => ({
        url: `/api/v1/project-groups/${id}/download`,
        method: 'GET',
      }),
    }),

    // Admin preview project group with API key
    getProjectGroupByIdAdmin: builder.query<
      IProjectGroupWithDetailsAdmin,
      { projectGroupId: string; token: string }
    >({
      query: ({ projectGroupId, token }) => ({
        url: `/api/v1/project-groups/admin/preview?projectGroupId=${projectGroupId}&token=${token}`,
        method: 'GET',
      }),
      transformResponse: (response: IAdminPreviewProjectGroupResponse) =>
        response.projectGroup,
    }),
  }),
});

// Configure cache tags for automatic refetching
apiSlice.enhanceEndpoints({
  addTagTypes: ['ProjectGroup', 'ProjectGroupList'],
  endpoints: {
    getProjectGroups: {
      providesTags: ['ProjectGroupList'],
    },
    getProjectGroupById: {
      providesTags: ['ProjectGroup'],
    },
    createProjectGroup: {
      invalidatesTags: ['ProjectGroupList', 'UserPosts'],
    },
    updateProjectGroup: {
      invalidatesTags: ['ProjectGroup', 'ProjectGroupList'],
    },
    updateProjectGroupProjects: {
      invalidatesTags: ['ProjectGroup', 'ProjectGroupList', 'UserPosts'],
    },
    deleteProjectGroup: {
      invalidatesTags: ['ProjectGroup', 'ProjectGroupList', 'UserPosts'],
    },
    submitProjectGroup: {
      invalidatesTags: ['ProjectGroup', 'ProjectGroupList', 'UserPosts'],
    },
  },
});

export const {
  useGetProjectGroupsQuery,
  useLazyGetProjectGroupsQuery,
  useGetProjectGroupByIdQuery,
  useLazyGetProjectGroupByIdQuery,
  useGetProjectDetailsQuery,
  useLazyGetProjectDetailsQuery,
  useGetProjectGroupPartsQuery,
  useLazyGetProjectGroupPartsQuery,
  useCreateProjectGroupMutation,
  useUpdateProjectGroupMutation,
  useUpdateProjectGroupProjectsMutation,
  useSubmitProjectGroupMutation,
  useDeleteProjectGroupMutation,
  useLazyDownloadProjectGroupQuery,
  useGetProjectGroupByIdAdminQuery,
} = projectGroupApiSlice;
